/**
 * Marker DOM, built and mutated without React.
 *
 * Everything here is a plain function over an element. The position path in
 * useDroneMarkers.ts runs at the broadcast rate for the whole fleet, so it must
 * not allocate a component, a props object or a virtual DOM node to move an
 * aircraft three metres. The predecessor re-rendered 1,136 lines of JSX for
 * exactly that.
 *
 * Marker styling lives in one injected stylesheet rather than in class strings
 * assembled per update. Tailwind cannot see classes that are built at runtime,
 * and a marker that silently loses its styling in a production build is worse
 * than one that never had any.
 */

import type { ConnectionState } from '@/types/domain';

const STYLE_ELEMENT_ID = 'drone-marker-styles';

/**
 * Marker sizing is fixed in pixels, not scaled with zoom.
 *
 * An operator zooms out to see the whole area of operations, and markers that
 * shrank with the map were unreadable at exactly the moment there was most to
 * read. 34px is the smallest size at which the heading arrow stays legible on a
 * 1080p panel in daylight.
 */
const MARKER_CSS = `
.dm-marker {
  position: relative;
  width: 34px;
  height: 34px;
  cursor: pointer;
  will-change: transform;
}

.dm-ring {
  position: absolute;
  inset: -6px;
  border-radius: 9999px;
  border: 2px solid transparent;
  transition: border-color 120ms linear;
}

.dm-marker[data-selected='true'] .dm-ring {
  border-color: var(--foreground);
}

.dm-body {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--marker-color, var(--nominal));
  transform: rotate(0deg);
  transform-origin: 50% 50%;
}

.dm-body svg {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 1px 2px rgb(0 0 0 / 0.85));
}

/* No position fix: the marker is parked somewhere meaningless, so it is not
   shown at all. An aircraft drawn at a place it is not is worse than one that
   is missing from the map. */
.dm-marker[data-fix='none'] { display: none; }

/* No heading fix: the arrow would be pointing at an arbitrary direction, so it
   is replaced by a disc that claims nothing. */
.dm-marker[data-heading='unknown'] .dm-arrow { display: none; }
.dm-marker[data-heading='known'] .dm-disc { display: none; }

.dm-marker[data-state='stale'] .dm-body,
.dm-marker[data-state='disconnected'] .dm-body {
  opacity: 0.45;
}

.dm-label {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translate(-50%, 3px);
  padding: 1px 4px;
  border-radius: 3px;
  background: rgb(0 0 0 / 0.72);
  color: var(--foreground);
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 10px;
  line-height: 14px;
  letter-spacing: 0.04em;
  white-space: nowrap;
  pointer-events: none;
}

.dm-marker[data-armed='true'] .dm-label {
  color: var(--armed);
}

.dm-battery {
  position: absolute;
  bottom: -9px;
  left: 50%;
  width: 26px;
  height: 3px;
  transform: translateX(-50%);
  border-radius: 2px;
  background: rgb(0 0 0 / 0.6);
  overflow: hidden;
  pointer-events: none;
}

.dm-battery-fill {
  height: 100%;
  width: 0%;
  background: var(--nominal);
  transition: width 200ms linear;
}
`;

/**
 * Inject the marker stylesheet once per document.
 *
 * Called from marker creation rather than at module load so that importing this
 * module in a test does not touch the DOM.
 */
export function ensureMarkerStyles(doc: Document = document): void {
  if (doc.getElementById(STYLE_ELEMENT_ID)) return;
  const style = doc.createElement('style');
  style.id = STYLE_ELEMENT_ID;
  style.textContent = MARKER_CSS;
  doc.head.appendChild(style);
}

const ARROW_SVG = `
<svg viewBox="0 0 24 24" aria-hidden="true">
  <g class="dm-arrow">
    <path d="M12 2 L20 21 L12 16.6 L4 21 Z" fill="currentColor" stroke="rgba(0,0,0,0.7)" stroke-width="1" stroke-linejoin="round"/>
  </g>
  <g class="dm-disc">
    <circle cx="12" cy="12" r="6" fill="currentColor" stroke="rgba(0,0,0,0.7)" stroke-width="1.5"/>
  </g>
</svg>`;

export interface DroneMarkerElement {
  /** What MapLibre positions. Never write a transform onto this. */
  root: HTMLDivElement;
  /** Rotated by heading, so it does not fight the translate on the root. */
  body: HTMLDivElement;
  label: HTMLDivElement;
  batteryFill: HTMLDivElement;
}

export function createDroneMarkerElement(callsign: string): DroneMarkerElement {
  ensureMarkerStyles();

  const root = document.createElement('div');
  root.className = 'dm-marker';
  root.dataset.heading = 'unknown';
  root.dataset.fix = 'none';
  root.dataset.state = 'connecting';
  root.dataset.armed = 'false';
  root.dataset.selected = 'false';
  root.setAttribute('role', 'button');
  root.setAttribute('aria-label', `${callsign} on map`);
  root.tabIndex = 0;

  const ring = document.createElement('div');
  ring.className = 'dm-ring';

  const body = document.createElement('div');
  body.className = 'dm-body';
  body.innerHTML = ARROW_SVG;

  const label = document.createElement('div');
  label.className = 'dm-label';
  label.textContent = callsign;

  const battery = document.createElement('div');
  battery.className = 'dm-battery';
  const batteryFill = document.createElement('div');
  batteryFill.className = 'dm-battery-fill';
  battery.appendChild(batteryFill);

  root.append(ring, body, label, battery);
  return { root, body, label, batteryFill };
}

/**
 * Point the marker.
 *
 * MapLibre owns `transform` on the marker root, so the rotation goes on the
 * inner element. Writing it on the root instead is how the predecessor's
 * markers jumped to the top-left corner of the viewport whenever a heading
 * arrived between two position fixes.
 */
export function applyMarkerHeading(element: DroneMarkerElement, headingDeg: number | null): void {
  if (headingDeg === null || Number.isNaN(headingDeg)) {
    element.root.dataset.heading = 'unknown';
    return;
  }
  element.root.dataset.heading = 'known';
  element.body.style.transform = `rotate(${headingDeg.toFixed(1)}deg)`;
}

/** Battery colour thresholds, in percent remaining. */
const BATTERY_CAUTION = 30;
const BATTERY_DANGER = 15;

export interface MarkerVisualState {
  connection: ConnectionState;
  armed: boolean;
  selected: boolean;
  batteryPercent: number | undefined;
  /** Team colour when the drone belongs to one, otherwise null. */
  color: string | null;
}

export function applyMarkerState(element: DroneMarkerElement, state: MarkerVisualState): void {
  const { root } = element;
  root.dataset.state = state.connection;
  root.dataset.armed = state.armed ? 'true' : 'false';
  root.dataset.selected = state.selected ? 'true' : 'false';
  root.style.setProperty('--marker-color', state.color ?? defaultMarkerColor(state));

  if (state.batteryPercent === undefined) {
    element.batteryFill.style.width = '0%';
    return;
  }
  const pct = Math.max(0, Math.min(100, state.batteryPercent));
  element.batteryFill.style.width = `${pct}%`;
  element.batteryFill.style.background = batteryColor(pct);
}

export function batteryColor(percent: number): string {
  if (percent <= BATTERY_DANGER) return 'var(--danger)';
  if (percent <= BATTERY_CAUTION) return 'var(--caution)';
  return 'var(--nominal)';
}

function defaultMarkerColor(state: MarkerVisualState): string {
  if (state.connection === 'disconnected') return 'var(--muted-foreground)';
  if (state.connection === 'stale') return 'var(--caution)';
  if (state.armed) return 'var(--armed)';
  return 'var(--nominal)';
}

/**
 * A stable colour for a drone with no team.
 *
 * Hue only, at a fixed lightness and chroma, so an unassigned aircraft still
 * gets a distinguishable track without joining the three colours that mean
 * something. The hash is deterministic: the same aircraft is the same colour on
 * every operator's screen, which matters when two of them are on the radio.
 */
export function trackColorFor(droneId: string): string {
  let hash = 0;
  for (let i = 0; i < droneId.length; i += 1) {
    hash = (hash * 31 + droneId.charCodeAt(i)) % 360;
  }
  // hsl, not oklch. This colour is not only CSS: it rides on a GeoJSON feature
  // into MapLibre paint properties ('line-color': ['get','color']) for the
  // track and route layers, and MapLibre's parser understands hex/rgb/hsl and
  // NOT oklch. An oklch value there logs "Could not parse color" per feature
  // and the line never draws.
  return `hsl(${hash}, 55%, 62%)`;
}
